import React, { useEffect, useMemo, useState } from "react";
import { Calendar, Loader2, Clock3, Video, Sparkles, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/auth";

const DURATIONS = [
  { v: 45, l: "45 min" },
  { v: 60, l: "1 hour" },
  { v: 120, l: "2 hours" },
  { v: 480, l: "Full day" },
];

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function StatusPill({ status }) {
  const map = {
    requested: ["bg-orange-100 text-orange-800", "Requested"],
    confirmed: ["bg-[#2D9B4E]/10 text-[#23803F]", "Confirmed"],
    completed: ["bg-gray-100 text-gray-700", "Completed"],
    cancelled: ["bg-red-100 text-red-700", "Cancelled"],
  };
  const [cls, lab] = map[status] || ["bg-gray-100 text-gray-700", status];
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${cls}`}>{lab}</span>;
}

export default function Sessions() {
  const [entitlement, setEntitlement] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [connections, setConnections] = useState([]);
  const [onCallQueue, setOnCallQueue] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ connection_id: "", duration_minutes: 60, scheduled_at: "" });
  const [onCallReason, setOnCallReason] = useState("");
  const [showOnCall, setShowOnCall] = useState(false);

  const refresh = async () => {
    try {
      const [ent, sess, conns] = await Promise.all([
        api.get("/buddy/entitlement").catch(() => null),
        api.get("/buddy/sessions"),
        api.get("/buddy/connections"),
      ]);
      setEntitlement(ent?.data?.entitlement || null);
      setSessions(sess.data.items || []);
      setConnections((conns.data.items || []).filter((c) => c.status === "accepted"));
      // Only Buddies see queue
      try {
        const q = await api.get("/buddy/on-call/queue");
        setOnCallQueue(q.data.items || []);
      } catch { setOnCallQueue(null); }
    } catch (err) {
      // silently swallow — surfaces empty state
    }
  };

  useEffect(() => { refresh(); }, []);

  const isMemberSide = connections.length > 0;
  const isBuddySide = onCallQueue !== null;

  const upcoming = useMemo(
    () => sessions.filter((x) => ["requested", "confirmed"].includes(x.status)),
    [sessions]
  );
  const history = useMemo(
    () => sessions.filter((x) => ["completed", "cancelled"].includes(x.status)),
    [sessions]
  );

  const createScheduled = async () => {
    if (!form.connection_id || !form.scheduled_at) {
      toast.error("Pick a Buddy and a time.");
      return;
    }
    setBusy(true);
    try {
      const iso = new Date(form.scheduled_at).toISOString();
      await api.post("/buddy/sessions", {
        connection_id: form.connection_id,
        duration_minutes: form.duration_minutes,
        scheduled_at: iso,
      });
      toast.success("Session requested. Your Buddy will confirm shortly.");
      setForm({ ...form, scheduled_at: "" });
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to request session.");
    } finally { setBusy(false); }
  };

  const act = async (sessionId, action) => {
    try {
      await api.post(`/buddy/sessions/${sessionId}/${action}`, {});
      toast.success(`Session ${action}ed.`);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed.");
    }
  };

  const createOnCall = async () => {
    if (onCallReason.trim().length < 5) {
      toast.error("Tell us briefly what's happening (5+ characters).");
      return;
    }
    setBusy(true);
    try {
      await api.post("/buddy/on-call", { reason: onCallReason.trim(), duration_minutes: 45 });
      toast.success("On-call request created. An available Buddy will pick it up.");
      setOnCallReason("");
      setShowOnCall(false);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed.");
    } finally { setBusy(false); }
  };

  const acceptOnCall = async (sessionId) => {
    try {
      await api.post(`/buddy/on-call/${sessionId}/accept`);
      toast.success("Accepted. You're now connected.");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Someone else grabbed it first.");
    }
  };

  return (
    <div data-testid="sessions-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0B132B]">Buddy sessions</h1>
          <p className="text-sm text-[#4B5563]">Schedule, join, or ask for help right now.</p>
        </div>
        {entitlement && (
          <div data-testid="sessions-entitlement" className="bg-white rounded-2xl border border-gray-100 px-5 py-3 flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-[#23803F]" />
            <div className="text-sm">
              <div className="font-bold text-[#0B132B]">{entitlement.remaining} of {entitlement.allocated} free sessions left</div>
              <div className="text-xs text-[#4B5563]">Resets on the 1st · Period {entitlement.period}</div>
            </div>
          </div>
        )}
      </div>

      {/* Member side: schedule + on-call */}
      {isMemberSide && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-gray-100" data-testid="sessions-schedule-card">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-[#1A5FAB]" />
              <h2 className="font-bold text-[#0B132B] text-lg">Schedule a session</h2>
            </div>
            <div className="space-y-3">
              <select
                data-testid="session-buddy-select"
                value={form.connection_id}
                onChange={(e) => setForm({ ...form, connection_id: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#2D9B4E] focus:outline-none text-sm"
              >
                <option value="">Choose your Buddy…</option>
                {connections.map((c) => (
                  <option key={c.id} value={c.id}>{c.buddy_name || "Buddy"}</option>
                ))}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input
                  data-testid="session-time-input"
                  type="datetime-local"
                  value={form.scheduled_at}
                  onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#2D9B4E] focus:outline-none text-sm"
                />
                <select
                  data-testid="session-duration-select"
                  value={form.duration_minutes}
                  onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value, 10) })}
                  className="w-full px-3 py-2.5 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#2D9B4E] focus:outline-none text-sm"
                >
                  {DURATIONS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
                </select>
              </div>
              <button
                data-testid="session-create-btn"
                disabled={busy}
                onClick={createScheduled}
                className="btc-btn-primary text-sm disabled:opacity-50"
              >
                Request session
              </button>
            </div>
          </div>
          <div className="bg-gradient-to-br from-[#1A5FAB] to-[#0B132B] rounded-2xl p-6 text-white" data-testid="oncall-card">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5" />
              <h2 className="font-bold text-lg">Need a Buddy now?</h2>
            </div>
            <p className="text-sm text-white/80">Send an on-call request. Any available Buddy can pick it up right away.</p>
            {!showOnCall ? (
              <button
                data-testid="oncall-open-btn"
                onClick={() => setShowOnCall(true)}
                className="mt-4 px-4 py-2 rounded-full bg-white text-[#1A5FAB] font-semibold text-sm"
              >
                Ask for help now
              </button>
            ) : (
              <div className="mt-4 space-y-2">
                <textarea
                  data-testid="oncall-reason"
                  rows={3}
                  value={onCallReason}
                  onChange={(e) => setOnCallReason(e.target.value)}
                  placeholder="A short note — what's going on?"
                  className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/30 placeholder-white/60 text-sm focus:outline-none"
                />
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setShowOnCall(false)} className="text-xs text-white/70 px-3 py-2">Cancel</button>
                  <button data-testid="oncall-submit" disabled={busy} onClick={createOnCall} className="px-4 py-2 rounded-full bg-white text-[#1A5FAB] font-semibold text-sm disabled:opacity-50">
                    Send
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Buddy side: on-call queue */}
      {isBuddySide && onCallQueue.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border-2 border-[#1A5FAB]" data-testid="oncall-queue-card">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-5 h-5 text-[#1A5FAB]" />
            <h2 className="font-bold text-[#0B132B] text-lg">Members needing a Buddy now</h2>
          </div>
          <div className="space-y-2">
            {onCallQueue.map((q) => (
              <div key={q.id} className="flex items-start gap-3 p-4 rounded-xl bg-[#F8F9FB]">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#0B132B]">{q.member_name || "Member"}</div>
                  <div className="text-sm text-[#4B5563] mt-1">{q.reason}</div>
                  <div className="text-xs text-[#4B5563] mt-1">{fmt(q.created_at)}</div>
                </div>
                <button
                  data-testid={`oncall-accept-${q.id}`}
                  onClick={() => acceptOnCall(q.id)}
                  className="px-3 py-2 rounded-full bg-[#2D9B4E] text-white text-sm font-semibold"
                >
                  Accept
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming */}
      <div>
        <h2 className="font-bold text-[#0B132B] text-lg mb-3">Upcoming</h2>
        {upcoming.length === 0 ? (
          <div className="bg-white/60 rounded-2xl p-8 text-center text-sm text-[#4B5563] border border-gray-100">
            No upcoming sessions yet.
          </div>
        ) : (
          <div className="space-y-2" data-testid="sessions-upcoming">
            {upcoming.map((sess) => (
              <SessionRow key={sess.id} sess={sess} onAct={act} />
            ))}
          </div>
        )}
      </div>

      {history.length > 0 && (
        <div>
          <h2 className="font-bold text-[#0B132B] text-lg mb-3">History</h2>
          <div className="space-y-2" data-testid="sessions-history">
            {history.map((sess) => (
              <SessionRow key={sess.id} sess={sess} onAct={act} readOnly />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SessionRow({ sess, onAct, readOnly }) {
  const isMember = sess.viewer_role === "member";
  // Show Join Session for confirmed scheduled sessions within a 10-min-before window
  const canJoin = (() => {
    if (sess.status !== "confirmed") return false;
    if (sess.session_type === "on_call") return true;
    if (!sess.scheduled_at) return false;
    const startMs = new Date(sess.scheduled_at).getTime();
    const endMs = new Date(sess.ends_at || (startMs + 60 * 60 * 1000)).getTime();
    const now = Date.now();
    return now >= startMs - 10 * 60 * 1000 && now <= endMs + 5 * 60 * 1000;
  })();
  return (
    <div className="bg-white rounded-2xl p-4 border border-gray-100 flex items-center gap-4">
      <div className="w-11 h-11 rounded-full bg-[#2D9B4E]/10 flex items-center justify-center">
        {sess.session_type === "on_call" ? <AlertCircle className="w-5 h-5 text-[#1A5FAB]" /> : <Video className="w-5 h-5 text-[#23803F]" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-[#0B132B]">
            {isMember ? (sess.buddy_name || "Your Buddy") : (sess.member_name || "Member")}
          </span>
          <StatusPill status={sess.status} />
          {sess.session_type === "on_call" && (
            <span className="text-[10px] uppercase tracking-wider text-[#1A5FAB] font-bold">On-call</span>
          )}
        </div>
        <div className="text-xs text-[#4B5563] mt-0.5 flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><Clock3 className="w-3 h-3" /> {sess.duration_label}</span>
          {sess.scheduled_at && <span>{fmt(sess.scheduled_at)}</span>}
        </div>
      </div>
      {!readOnly && (
        <div className="flex gap-2 flex-wrap">
          {canJoin && (
            <a
              data-testid={`session-join-${sess.id}`}
              href={`/portal/sessions/${sess.id}/room`}
              className="px-3 py-1.5 rounded-full bg-[#2D9B4E] text-white text-sm font-semibold inline-flex items-center gap-1"
            >
              <Video className="w-3.5 h-3.5" /> Join Session
            </a>
          )}
          {sess.status === "requested" && !isMember && sess.session_type === "scheduled" && (
            <button data-testid={`session-confirm-${sess.id}`} onClick={() => onAct(sess.id, "confirm")} className="px-3 py-1.5 rounded-full bg-[#2D9B4E] text-white text-sm">
              <CheckCircle2 className="w-3.5 h-3.5 inline" /> Confirm
            </button>
          )}
          {["requested", "confirmed"].includes(sess.status) && (
            <button data-testid={`session-cancel-${sess.id}`} onClick={() => onAct(sess.id, "cancel")} className="px-3 py-1.5 rounded-full text-sm text-red-700 border border-red-200">
              <XCircle className="w-3.5 h-3.5 inline" /> Cancel
            </button>
          )}
          {sess.status === "confirmed" && !isMember && (
            <button data-testid={`session-complete-${sess.id}`} onClick={() => onAct(sess.id, "complete")} className="px-3 py-1.5 rounded-full bg-[#1A5FAB] text-white text-sm">
              Mark complete
            </button>
          )}
        </div>
      )}
    </div>
  );
}
